import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { isCancelled } from '../api';
import { apiError } from '../format';
import { rulesApi } from './rulesApi';
import { BORDER, DARK, MUTED, PERM_HINTS_RULES, PRIMARY, SOFT } from './rulesConstants';
import { CloseButton } from './RuleBits';
import NetworkRulesTab from './NetworkRulesTab';
import ExceptionsTab from './ExceptionsTab';
import RuleEventsTab from './RuleEventsTab';
import RuleAuditTab from './RuleAuditTab';
import RuleSimulatorTab from './RuleSimulatorTab';
import { RulesOverview } from './rulesTypes';

type RulesTab = 'rules' | 'exceptions' | 'events' | 'audit' | 'simulate';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Счётчик в подписи вкладки */
const CountBadge: React.FC<{ n: number }> = ({ n }) => (
  <Box
    component="span"
    sx={{
      minWidth: 20, height: 20, px: 0.75, borderRadius: '10px', bgcolor: '#e2e8f0', color: '#475569', fontSize: 11.5, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ml: 1
    }}
  >
    {n}
  </Box>
);

/** Диалог «Настройки документов» (шестерёнка в разделе «Документы»): правила, исключения, журналы, проверка. PROJECT_NOTES §23 */
const RulesDialog: React.FC<Props> = ({ open, onClose }) => {
  const [tab, setTab] = useState<RulesTab>('rules');
  const [overview, setOverview] = useState<RulesOverview | null>(null);
  const [error, setError] = useState<{ text: string; forbidden: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [logKey, setLogKey] = useState(0);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    return rulesApi.overview(signal)
      .then(res => { setOverview(res); setError(null); })
      .catch(err => {
        if (isCancelled(err)) return;
        setError({ text: apiError(err, 'Не удалось загрузить настройки'), forbidden: err?.response?.status === 403 });
      })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [open, load]);

  /** После изменения правил/исключений: перечитываем список и обновляем журнал изменений */
  const changed = () => { setLogKey(k => k + 1); load(); };

  const readOnlyNetwork = overview && !overview.canEditNetwork;
  const activeExceptions = overview ? overview.exceptions.filter(e => !e.expired).length : 0;

  return (
    <Dialog
      open={open} onClose={onClose} fullWidth maxWidth="lg"
      slotProps={{
        paper: { sx: { height: '88vh', borderRadius: '12px', bgcolor: SOFT, backgroundImage: 'none', overflow: 'hidden' } },
        backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.45)' } }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, pt: 2, pb: 0, bgcolor: '#fff' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: DARK, fontSize: 19, lineHeight: 1.3 }}>Настройки документов</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>Правила проверки при создании перемещений и возвратов</Typography>
        </Box>
        {loading && overview && <CircularProgress size={18} sx={{ color: PRIMARY }} />}
        {readOnlyNetwork && (
          <Tooltip title={PERM_HINTS_RULES.networkReadOnly} arrow>
            <Box
              sx={{
                display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', px: 1.25, height: 26, borderRadius: '6px',
                border: `1px solid ${BORDER}`, bgcolor: SOFT, color: MUTED, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              Правила сети - только просмотр
            </Box>
          </Tooltip>
        )}
        <CloseButton onClick={onClose} />
      </Box>

      <Box sx={{ px: 3, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, maxWidth: '100%' }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0', bgcolor: PRIMARY },
            '& .MuiTab-root': {
              textTransform: 'none', minHeight: 44, px: 0.5, mr: 3, minWidth: 0, fontWeight: 600, fontSize: 14, color: MUTED,
              '&:hover': { color: DARK },
              '&.Mui-selected': { color: PRIMARY }
            }
          }}
        >
          <Tab value="rules" label="Правила" disableRipple />
          <Tab
            value="exceptions" disableRipple
            label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>Исключения{activeExceptions > 0 && <CountBadge n={activeExceptions} />}</Box>}
          />
          <Tab value="events" label="Журнал срабатываний" disableRipple />
          <Tab value="audit" label="История изменений" disableRipple />
          <Tab value="simulate" label="Проверка" disableRipple />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: SOFT }}>
        {error && !overview && (
          <Alert
            severity={error.forbidden ? 'warning' : 'error'} sx={{ borderRadius: '8px' }}
            action={!error.forbidden ? <Button color="inherit" size="small" onClick={() => load()}>Повторить</Button> : undefined}
          >
            {error.forbidden ? 'У вас нет доступа к настройкам документов. Обратитесь к администратору.' : error.text}
          </Alert>
        )}
        {!overview && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>}
        {overview && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error.text}</Alert>}
            {tab === 'rules' && <NetworkRulesTab overview={overview} onSaved={changed} />}
            {tab === 'exceptions' && <ExceptionsTab overview={overview} onChanged={changed} />}
            {tab === 'events' && <RuleEventsTab catalog={overview.catalog} refreshKey={logKey} />}
            {tab === 'audit' && <RuleAuditTab catalog={overview.catalog} refreshKey={logKey} />}
            {tab === 'simulate' && <RuleSimulatorTab overview={overview} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RulesDialog;
